import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Stack,
} from '@mui/material';
import { Visibility, VisibilityOff, AppRegistration, Home, Login } from '@mui/icons-material';
import { authService } from '@smartgrow/shared';

export const RegisterPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    email: '',
    phone: '',
    farmName: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError('');
  };

  const validate = () => {
    if (!form.username.trim()) return 'Username is required';
    if (form.username.length < 3) return 'Username must be at least 3 characters';
    if (!form.password) return 'Password is required';
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) return 'Passwords do not match';
    if (!form.fullName.trim()) return 'Full name is required';
    if (!form.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Invalid email address';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authService.register({
        username: form.username.trim(),
        password: form.password,
        fullName: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        farmName: form.farmName.trim() || undefined,
      });
      navigate('/dashboard');
    } catch (err: any) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Registration failed. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button
            startIcon={<Home />}
            onClick={() => navigate('/')}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
            variant="outlined"
          >
            Home
          </Button>
        </Box>

        <Card elevation={8}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                SmartGrow
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Create your farm account
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={form.fullName}
                  onChange={handleChange('fullName')}
                  disabled={loading}
                  required
                  autoFocus
                />

                <TextField
                  fullWidth
                  label="Farm Name (optional)"
                  value={form.farmName}
                  onChange={handleChange('farmName')}
                  disabled={loading}
                  placeholder="e.g. Green Acres Farm"
                  helperText="Defaults to your name + 's Farm' if left blank"
                />

                <Divider />

                <TextField
                  fullWidth
                  label="Username"
                  value={form.username}
                  onChange={handleChange('username')}
                  disabled={loading}
                  required
                  inputProps={{ minLength: 3 }}
                />

                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  disabled={loading}
                  required
                />

                <TextField
                  fullWidth
                  label="Phone Number (optional)"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  disabled={loading}
                  placeholder="+234 800 000 0000"
                />

                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange('password')}
                  disabled={loading}
                  required
                  helperText="At least 8 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  disabled={loading}
                  required
                  error={!!form.confirmPassword && form.password !== form.confirmPassword}
                  helperText={
                    form.confirmPassword && form.password !== form.confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={<AppRegistration />}
                  sx={{ py: 1.5, mt: 1 }}
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?{' '}
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<Login />}
                      onClick={() => navigate('/login')}
                      sx={{ textTransform: 'none' }}
                    >
                      Login
                    </Button>
                  </Typography>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};
